import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    IonModal,
    IonIcon,
    IonSpinner,
    IonToast,
    IonBackButton,
    IonSegment,
    IonSegmentButton,
    IonLabel,
} from "@ionic/react";
import {
    closeOutline,
    checkmarkCircleOutline,
    closeCircleOutline,
    documentTextOutline,
    timeOutline,
    cashOutline,
    cubeOutline,
} from "ionicons/icons";
import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Typography,
    Box,
    Alert,
    TablePagination,
    Card,
    CardContent,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    CircularProgress,
    TextField,
    InputAdornment,
} from "@mui/material";
import { Search, CheckCircle, Cancel, Visibility } from "@mui/icons-material";
import axios from "axios";
import { getAppSettings, timeAgo, baseUrl } from "../../../common/utils";
import { getAllPurchaseOrderRequests, getPendingCMDApprovals, getVendorsPurchaseOrderRequests } from "../../../api/accounting/accounting";

/* ================= TYPES ================= */

interface ApiItem {
    quantity: number;
    unit_price: string;
    name: string;
    product_description: string;
}

interface ApiOrder {
    id: string;
    purchase_id: string;
    name: string;
    status: "Accepted" | "Approved" | "Pending" | "Rejected";
    total_price: string;
    date_created: string;
    number_of_items: number;
    items: ApiItem[];
}

interface ApiResponse {
    total: number;
    data: ApiOrder[];
}

interface OrderItem {
    productName: string;
    description: string;
    quantity: number;
    unitPrice: number;
}

interface PurchaseOrder {
    id: string;
    purchaseId: string;
    name: string;
    price: number;
    status: string;
    dateCreated: string;
    itemCount: number;
    items: OrderItem[];
}

/* ================= COMPONENT ================= */

const PurchaseOrderDashboard = () => {
    const { siteName } = getAppSettings();

    const [orders, setOrders] = useState<PurchaseOrder[]>([]);
    const [filteredOrders, setFilteredOrders] = useState<PurchaseOrder[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState<"pending" | "accepted" | "all">("pending");

    // --- Pagination States ---
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    /* ================= FETCH DATA ================= */

    useEffect(() => {
        fetchOrders();
    }, [page, rowsPerPage]);

    useEffect(() => {
        applyFilters();
    }, [orders, searchQuery, activeTab]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                page: (page + 1).toString(),
                per_page: rowsPerPage.toString(),
            };

            const response = await getPendingCMDApprovals(params);
            
            console.log('Purchase Orders Response:', response);

            if (response && response.data && response.data) {
                const apiData = response.data.data;
                setTotalCount(response.data.total || apiData.length);

                const mappedOrders: PurchaseOrder[] = apiData.map((order: any) => ({
                    id: order.id ? order.id.toString() : '',
                    purchaseId: order.po_number || order.purchase_id || order.id?.toString(),
                    name: order.requested_by_name || order.name || 'Unknown',
                    price: Number(order.total_value || order.total_price || 0),
                    status: order.status || 'Pending',
                    dateCreated: order.created_at || order.date_created,
                    itemCount: order.items ? order.items.length : (order.number_of_items || 0),
                    items: order.items ? order.items.map((item: any) => ({
                        productName: item.stock_name || item.name || 'Unknown Item',
                        description: item.status || item.product_description || '',
                        quantity: Number(item.requested_qty || item.quantity || 0),
                        unitPrice: Number(item.unit_price || 0),
                    })) : [],
                }));

                setOrders(mappedOrders);
            } else {
                setOrders([]);
            }
        } catch (err: any) {
            console.error('Error fetching orders:', err);
            const errorMessage = err.response?.data?.message || err.message || "Something went wrong fetching orders";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...orders];

        // Filter by tab
        if (activeTab === "pending") {
            filtered = filtered.filter(o => o.status.toLowerCase() === "pending");
        } else if (activeTab === "accepted") {
            filtered = filtered.filter(o => o.status.toLowerCase() === "accepted");
        }

        // Filter by search
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(
                o =>
                    o.name.toLowerCase().includes(query) ||
                    o.purchaseId.toLowerCase().includes(query)
            );
        }

        setFilteredOrders(filtered);
    };

    /* ================= ACTIONS ================= */

    const handleOrderAction = async (
        action: "accept" | "reject",
        purchaseId: string
    ) => {
        try {
            setProcessingId(action);

            const url = `${baseUrl()}order/${action}?purchase_id=${purchaseId}`;
            await axios.get(url);

            const newStatus = action === "accept" ? "Accepted" : "Rejected";

            setOrders((prev) =>
                prev.map((o) =>
                    o.purchaseId === purchaseId
                        ? { ...o, status: newStatus }
                        : o
                )
            );

            if (selectedOrder && selectedOrder.purchaseId === purchaseId) {
                setSelectedOrder((prev) =>
                    prev
                        ? { ...prev, status: newStatus }
                        : null
                );
            }

            setToastMessage(`Order ${action}ed successfully!`);
            setShowModal(false);
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || `Failed to ${action} order`;
            setToastMessage(`Error: ${errorMessage}`);
        } finally {
            setProcessingId(null);
        }
    };

    /* ================= HELPERS ================= */

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case "accepted":
            case "approved":
                return "success";
            case "rejected":
                return "error";
            case "pending":
                return "warning";
            default:
                return "default";
        }
    };

    const handleOpenModal = (order: PurchaseOrder) => {
        setSelectedOrder(order);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedOrder(null);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    /* ================= STATS ================= */

    const stats = {
        total: orders.length,
        pending: orders.filter(o => o.status.toLowerCase() === "pending").length,
        accepted: orders.filter(o => o.status.toLowerCase() === "accepted").length,
        rejected: orders.filter(o => o.status.toLowerCase() === "rejected").length,
    };

    /* ================= RENDER ================= */

    return (
        <IonPage className="pushDown">
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton defaultHref="/dashboard" />
                    </IonButtons>
                    <IonTitle>Vendor Dashboard</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="ion-padding">
                {/* Header Section */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
                        Purchase Orders
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                        View and manage orders assigned to you after CMD approval
                    </Typography>
                </Box>

                {/* Stats Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={3}>
                        <Card sx={{ bgcolor: "#f8fafc", borderLeft: "4px solid #3b82f6" }}>
                            <CardContent sx={{ py: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <IonIcon icon={documentTextOutline} style={{ fontSize: 24, color: "#3b82f6" }} />
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                            {stats.total}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                                            Total Orders
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card sx={{ bgcolor: "#fef3c7", borderLeft: "4px solid #f59e0b" }}>
                            <CardContent sx={{ py: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <IonIcon icon={timeOutline} style={{ fontSize: 24, color: "#f59e0b" }} />
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                            {stats.pending}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                                            Pending
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card sx={{ bgcolor: "#d1fae5", borderLeft: "4px solid #10b981" }}>
                            <CardContent sx={{ py: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <IonIcon icon={checkmarkCircleOutline} style={{ fontSize: 24, color: "#10b981" }} />
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                            {stats.accepted}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                                            Accepted
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card sx={{ bgcolor: "#fee2e2", borderLeft: "4px solid #ef4444" }}>
                            <CardContent sx={{ py: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <IonIcon icon={closeCircleOutline} style={{ fontSize: 24, color: "#ef4444" }} />
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                            {stats.rejected}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                                            Rejected
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Filters and Search */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search by order name or ID..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IonSegment
                                    value={activeTab}
                                    onIonChange={(e) => setActiveTab(e.detail.value as any)}
                                >
                                    <IonSegmentButton value="pending">
                                        <IonLabel>Pending</IonLabel>
                                    </IonSegmentButton>
                                    <IonSegmentButton value="accepted">
                                        <IonLabel>Accepted</IonLabel>
                                    </IonSegmentButton>
                                    <IonSegmentButton value="all">
                                        <IonLabel>All</IonLabel>
                                    </IonSegmentButton>
                                </IonSegment>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {/* Orders Table */}
                <Paper sx={{ borderRadius: 2, overflow: "hidden", boxShadow: 3 }}>
                    {loading && orders.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 600 }}>Order ID</TableCell>
                                            <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 600 }}>Requested By</TableCell>
                                            <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 600 }}>Status</TableCell>
                                            <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 600 }} align="right">Total Amount</TableCell>
                                            <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 600 }} align="center">Items</TableCell>
                                            <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 600 }}>Date</TableCell>
                                            <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 600 }} align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {filteredOrders.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ py: 6, color: "#94a3b8" }}>
                                                    No orders found
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredOrders.map((order) => (
                                                <TableRow key={order.purchaseId} hover>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {order.purchaseId}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">{order.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {timeAgo(order.dateCreated)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={order.status}
                                                            color={getStatusColor(order.status) as any}
                                                            size="small"
                                                            sx={{ fontWeight: 500 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" fontWeight={600}>
                                                            ₦{order.price.toLocaleString()}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={order.itemCount} size="small" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(order.dateCreated).toLocaleDateString()}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<Visibility />}
                                                            onClick={() => handleOpenModal(order)}
                                                        >
                                                            View
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={totalCount}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                            />
                        </>
                    )}
                </Paper>

                {/* ================= MODAL ================= */}

                <Dialog
                    open={showModal}
                    onClose={handleCloseModal}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{ bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="h6" fontWeight={600}>
                                Order Details
                            </Typography>
                            <Button onClick={handleCloseModal} size="small">
                                <IonIcon icon={closeOutline} />
                            </Button>
                        </Box>
                    </DialogTitle>

                    <DialogContent sx={{ mt: 2 }}>
                        {selectedOrder && (
                            <>
                                {/* Order Summary */}
                                <Card sx={{ mb: 3, bgcolor: "#f8fafc" }}>
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Order ID
                                                </Typography>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {selectedOrder.purchaseId}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Requested By
                                                </Typography>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {selectedOrder.name}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Status
                                                </Typography>
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Chip
                                                        label={selectedOrder.status}
                                                        color={getStatusColor(selectedOrder.status) as any}
                                                        size="small"
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Total Amount
                                                </Typography>
                                                <Typography variant="h6" color="primary" fontWeight={700}>
                                                    ₦{selectedOrder.price.toLocaleString()}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>

                                {/* Items List */}
                                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                                    Order Items ({selectedOrder.items.length})
                                </Typography>

                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedOrder.items.map((item, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {item.productName}
                                                        </Typography>
                                                        {item.description && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {item.description}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">{item.quantity}</TableCell>
                                                    <TableCell align="right">₦{item.unitPrice.toLocaleString()}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        ₦{(item.quantity * item.unitPrice).toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </DialogContent>

                    {selectedOrder && selectedOrder.status.toLowerCase() === "pending" && (
                        <DialogActions sx={{ p: 2, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={processingId === "reject" ? <CircularProgress size={16} /> : <Cancel />}
                                disabled={!!processingId}
                                onClick={() => handleOrderAction("reject", selectedOrder.purchaseId)}
                            >
                                Reject Order
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={processingId === "accept" ? <CircularProgress size={16} /> : <CheckCircle />}
                                disabled={!!processingId}
                                onClick={() => handleOrderAction("accept", selectedOrder.purchaseId)}
                            >
                                Accept Order
                            </Button>
                        </DialogActions>
                    )}
                </Dialog>

                <IonToast
                    isOpen={!!toastMessage}
                    message={toastMessage || ""}
                    duration={2000}
                    onDidDismiss={() => setToastMessage(null)}
                    position="bottom"
                    color={toastMessage?.includes("Error") ? "danger" : "success"}
                />
            </IonContent>
        </IonPage>
    );
};

export default PurchaseOrderDashboard;